/**
 * Guest services a hotel can switch on: housekeeping requests, laundry and the
 * restaurant menu. Configured by the owner under Manage → Settings → Services.
 *
 * Two layers, on purpose.
 *
 * The on/off flag for each service lives on `hotels/{hotelId}` as
 * `houseKeepingService` / `laundryService` / `restaurantService`. Every screen
 * already holds a live subscription to that document, so a switch there is
 * visible everywhere the moment it lands — and a guest page that asks "does
 * this hotel take laundry?" needs one read, not three.
 *
 * What each service *offers* lives under `hotels/{hotelId}/settings/…`:
 *
 *   settings/housekeeping   { enabled, services: HousekeepingServiceItem[] }
 *   settings/laundry        { enabled, services: LaundryServiceItem[] }
 *   settings/menu           { enabled, categories: string[] }
 *   settings/menu/items/*   one MenuItemDoc per dish
 *
 * Housekeeping and laundry keep their handful of rows inline — nine rows of
 * name-and-price is one document, not a collection. A restaurant menu is not:
 * it runs to hundreds of dishes edited one at a time, so each is its own
 * document and a price change rewrites one dish rather than the whole menu.
 *
 * The `enabled` on each settings document mirrors the hotel flag. It is
 * written in the same transaction, never on its own, so the two cannot drift;
 * it is there so the settings document is self-describing on its own.
 *
 * Defaults are seeded when the hotel is created and again, lazily, the first
 * time a settings document is written for a hotel that predates this feature.
 * After that the document is the source of truth: the predefined names below
 * are a starting point the owner may rename or delete, not a vocabulary the
 * app checks against.
 */

export const HOTEL_SERVICE = {
  HOUSEKEEPING: 'housekeeping',
  LAUNDRY: 'laundry',
  RESTAURANT: 'restaurant',
  WIFI: 'wifi',
  STORE: 'store',
  PLACES_TO_EXPLORE: 'placesToExplore',
} as const;

export type HotelService = (typeof HOTEL_SERVICE)[keyof typeof HOTEL_SERVICE];

export const HOTEL_SERVICES: readonly HotelService[] = [
  HOTEL_SERVICE.HOUSEKEEPING,
  HOTEL_SERVICE.RESTAURANT,
  HOTEL_SERVICE.LAUNDRY,
  HOTEL_SERVICE.WIFI,
  HOTEL_SERVICE.STORE,
  HOTEL_SERVICE.PLACES_TO_EXPLORE,
];

/**
 * The field on `hotels/{hotelId}` that carries each service's flag. The
 * spelling `houseKeepingService` is the documented one; do not "fix" it.
 */
export const HOTEL_SERVICE_FLAG: Readonly<Record<HotelService, string>> = {
  housekeeping: 'houseKeepingService',
  laundry: 'laundryService',
  restaurant: 'restaurantService',
  wifi: 'wifiService',
  store: 'storeService',
  placesToExplore: 'placesToExploreService',
};

/** What a hotel starts with: housekeeping on, everything else off. */
export const DEFAULT_HOTEL_SERVICE_FLAGS = {
  houseKeepingService: true,
  restaurantService: false,
  laundryService: false,
  wifiService: false,
  storeService: false,
  placesToExploreService: false,
} as const;

export interface HotelServiceFlags {
  houseKeepingService: boolean;
  restaurantService: boolean;
  laundryService: boolean;
  wifiService: boolean;
  storeService: boolean;
  placesToExploreService: boolean;
}

/**
 * The flags as a screen should read them. A hotel created before this feature
 * has none of the three fields; it is treated as the documented default rather
 * than as "all off", so an older hotel's housekeeping is not silently switched
 * off by an upgrade. Anything not literally `true`/`false` is ignored.
 */
export function resolveHotelServiceFlags(
  hotel: Partial<Record<keyof HotelServiceFlags, unknown>> | null | undefined,
): HotelServiceFlags {
  const pick = (key: keyof HotelServiceFlags) => {
    const value = hotel?.[key];
    return typeof value === 'boolean' ? value : DEFAULT_HOTEL_SERVICE_FLAGS[key];
  };
  return {
    houseKeepingService: pick('houseKeepingService'),
    restaurantService: pick('restaurantService'),
    laundryService: pick('laundryService'),
    wifiService: pick('wifiService'),
    storeService: pick('storeService'),
    placesToExploreService: pick('placesToExploreService'),
  };
}

export function isHotelServiceEnabled(
  hotel: Partial<Record<keyof HotelServiceFlags, unknown>> | null | undefined,
  service: HotelService,
): boolean {
  return resolveHotelServiceFlags(hotel)[
    HOTEL_SERVICE_FLAG[service] as keyof HotelServiceFlags
  ];
}

/* -------------------------------------------------------------------------- */
/* Free / paid                                                                */
/* -------------------------------------------------------------------------- */

export const SERVICE_CHARGE_TYPE = {
  FREE: 'free',
  PAID: 'paid',
} as const;

export type ServiceChargeType =
  (typeof SERVICE_CHARGE_TYPE)[keyof typeof SERVICE_CHARGE_TYPE];

export const SERVICE_CHARGE_TYPES: readonly ServiceChargeType[] = [
  SERVICE_CHARGE_TYPE.FREE,
  SERVICE_CHARGE_TYPE.PAID,
];

/** Laundry is priced per garment or per kilo; the owner picks per service. */
export const PRICING_UNIT = {
  ITEM: 'item',
  KG: 'kg',
} as const;

export type PricingUnit = (typeof PRICING_UNIT)[keyof typeof PRICING_UNIT];

export const PRICING_UNITS: readonly PricingUnit[] = [PRICING_UNIT.ITEM, PRICING_UNIT.KG];

/** A price in whole currency units with at most two decimals, so 12.50 is fine and 12.505 is not. */
export const MAX_SERVICE_CHARGE = 1_000_000;

export const MAX_SERVICE_NAME_LENGTH = 60;

/** Generous for a hotel; a list longer than this is a menu, not a service card. */
export const MAX_SERVICE_ITEMS = 100;

/* -------------------------------------------------------------------------- */
/* Housekeeping                                                               */
/* -------------------------------------------------------------------------- */

/** One row on the housekeeping card: "Extra Towels", free or ₹50. */
export interface HousekeepingServiceItem {
  /** Stable id; the predefined ones are readable, custom ones are generated. */
  id: string;
  name: string;
  enabled: boolean;
  type: ServiceChargeType;
  /** Always present; 0 when `type` is free. */
  charge: number;
}

/** `hotels/{hotelId}/settings/housekeeping` */
export interface HousekeepingSettingsDoc {
  enabled: boolean;
  services: HousekeepingServiceItem[];
  updatedAt?: unknown;
}

export const DEFAULT_HOUSEKEEPING_SERVICES: readonly HousekeepingServiceItem[] = [
  { id: 'drinking_water', name: 'Drinking Water', enabled: true, type: 'free', charge: 0 },
  { id: 'room_cleaning', name: 'Room Cleaning', enabled: true, type: 'free', charge: 0 },
  { id: 'extra_towels', name: 'Extra Towels', enabled: true, type: 'free', charge: 0 },
  { id: 'extra_pillows', name: 'Extra Pillows', enabled: true, type: 'free', charge: 0 },
  { id: 'extra_blankets', name: 'Extra Blankets', enabled: true, type: 'free', charge: 0 },
  {
    id: 'replacing_toilet_paper',
    name: 'Replacing Toilet Paper',
    enabled: true,
    type: 'free',
    charge: 0,
  },
  { id: 'replacing_towels', name: 'Replacing Towels', enabled: true, type: 'free', charge: 0 },
  {
    id: 'replacing_bed_covers',
    name: 'Replacing Bed Covers/Pillow Covers',
    enabled: true,
    type: 'free',
    charge: 0,
  },
  { id: 'replacing_blankets', name: 'Replacing Blankets', enabled: true, type: 'free', charge: 0 },
];

/* -------------------------------------------------------------------------- */
/* Laundry                                                                    */
/* -------------------------------------------------------------------------- */

export interface LaundryServiceItem extends HousekeepingServiceItem {
  pricingUnit: PricingUnit;
}

/** `hotels/{hotelId}/settings/laundry` */
export interface LaundrySettingsDoc {
  enabled: boolean;
  services: LaundryServiceItem[];
  updatedAt?: unknown;
}

export const DEFAULT_LAUNDRY_SERVICES: readonly LaundryServiceItem[] = [
  { id: 'laundry', name: 'Laundry', enabled: true, type: 'free', charge: 0, pricingUnit: 'item' },
  {
    id: 'dry_cleaning',
    name: 'Dry Cleaning',
    enabled: true,
    type: 'free',
    charge: 0,
    pricingUnit: 'kg',
  },
  { id: 'ironing', name: 'Ironing', enabled: true, type: 'free', charge: 0, pricingUnit: 'item' },
];

/**
 * The two inline catalogues share one shape and one pair of callables
 * (`saveServiceItem` / `deleteServiceItem`, keyed by `kind`). Laundry rows
 * carry `pricingUnit` in addition.
 */
export const SERVICE_CATALOG_KIND = {
  HOUSEKEEPING: 'housekeeping',
  LAUNDRY: 'laundry',
} as const;

export type ServiceCatalogKind =
  (typeof SERVICE_CATALOG_KIND)[keyof typeof SERVICE_CATALOG_KIND];

export const SERVICE_CATALOG_KINDS: readonly ServiceCatalogKind[] = [
  SERVICE_CATALOG_KIND.HOUSEKEEPING,
  SERVICE_CATALOG_KIND.LAUNDRY,
];

export type ServiceCatalogItem = HousekeepingServiceItem | LaundryServiceItem;

/**
 * The catalogue a screen should render. Missing document → the defaults, with
 * `enabled` taken from the hotel flag the caller already holds, so a hotel
 * that predates this feature shows the same nine rows a new one does. Once the
 * owner saves anything, the document is written in full and this returns it
 * verbatim — the defaults are never merged back in.
 */
export function resolveServiceCatalog(
  kind: ServiceCatalogKind,
  doc: { enabled?: unknown; services?: unknown } | null | undefined,
  hotelFlagEnabled: boolean,
): { enabled: boolean; services: ServiceCatalogItem[] } {
  const defaults =
    kind === SERVICE_CATALOG_KIND.LAUNDRY
      ? DEFAULT_LAUNDRY_SERVICES
      : DEFAULT_HOUSEKEEPING_SERVICES;

  if (!doc || !Array.isArray(doc.services)) {
    return { enabled: hotelFlagEnabled, services: defaults.map((item) => ({ ...item })) };
  }
  return {
    enabled: typeof doc.enabled === 'boolean' ? doc.enabled : hotelFlagEnabled,
    services: doc.services as ServiceCatalogItem[],
  };
}

/* -------------------------------------------------------------------------- */
/* Restaurant menu                                                            */
/* -------------------------------------------------------------------------- */

export const MAX_MENU_CATEGORY_LENGTH = 40;
export const MAX_MENU_CATEGORIES = 30;
export const MAX_MENU_ITEM_NAME_LENGTH = 80;
export const MAX_MENU_ITEM_DESCRIPTION_LENGTH = 300;
export const MAX_MENU_ITEM_PRICE = 1_000_000;
/** Minutes. Anything longer is not "preparation time", it is a reservation. */
export const MAX_PREPARATION_MINUTES = 240;
/** Photos per dish. Enough for the plate, a close-up and a serving; more is a gallery. */
export const MAX_MENU_ITEM_IMAGES = 4;

export const DEFAULT_MENU_CATEGORIES: readonly string[] = [
  'Breakfast',
  'Starters',
  'Main Course',
  'Rice & Noodles',
  'Beverages',
  'Desserts',
];

/**
 * `hotels/{hotelId}/settings/menu`
 *
 * Categories are a list on the menu document the way room types are a list on
 * the hotel document: appended and renamed through callables that dedupe
 * case-insensitively inside a transaction, so "desserts" cannot join
 * "Desserts", and never deleted while a dish still uses one.
 */
export interface MenuSettingsDoc {
  enabled: boolean;
  categories: string[];
  updatedAt?: unknown;
}

/**
 * A category list document (`settings/menu`, `settings/store`), with the
 * defaults for a hotel that has never written one.
 */
export function resolveCategorySettings(
  doc: { enabled?: unknown; categories?: unknown } | null | undefined,
  hotelFlagEnabled: boolean,
  defaults: readonly string[],
): { enabled: boolean; categories: string[] } {
  if (!doc || !Array.isArray(doc.categories)) {
    return { enabled: hotelFlagEnabled, categories: [...defaults] };
  }
  return {
    enabled: typeof doc.enabled === 'boolean' ? doc.enabled : hotelFlagEnabled,
    categories: doc.categories.filter((entry): entry is string => typeof entry === 'string'),
  };
}

export function resolveMenuSettings(
  doc: { enabled?: unknown; categories?: unknown } | null | undefined,
  hotelFlagEnabled: boolean,
): { enabled: boolean; categories: string[] } {
  return resolveCategorySettings(doc, hotelFlagEnabled, DEFAULT_MENU_CATEGORIES);
}

/** `hotels/{hotelId}/settings/menu/items/{itemId}` */
export interface MenuItemDoc {
  name: string;
  category: string;
  price: number;
  enabled: boolean;
  description?: string;
  /** Undefined when the hotel does not mark dishes; true/false when it does. */
  vegetarian?: boolean;
  /** Minutes from order to table. */
  preparationMinutes?: number;
  /**
   * Photos as Storage paths under `menuImageStoragePath(hotelId)`, at most
   * `MAX_MENU_ITEM_IMAGES` — paths and never download URLs, for the same
   * reason as room media. The first one is the thumbnail.
   */
  images?: MenuImageRef[];
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface MenuImageRef {
  path: string;
}

/* -------------------------------------------------------------------------- */
/* Wi-Fi                                                                      */
/* -------------------------------------------------------------------------- */

export const WIFI_SECURITY_TYPE = {
  OPEN: 'Open',
  WPA2_PERSONAL: 'WPA2-Personal',
  WPA3_PERSONAL: 'WPA3-Personal',
  WPA2_WPA3_PERSONAL: 'WPA2/WPA3-Personal',
  WPA2_ENTERPRISE: 'WPA2-Enterprise',
  WPA3_ENTERPRISE: 'WPA3-Enterprise',
} as const;

export type WifiSecurityType = (typeof WIFI_SECURITY_TYPE)[keyof typeof WIFI_SECURITY_TYPE];

export const WIFI_SECURITY_TYPES: readonly WifiSecurityType[] = [
  WIFI_SECURITY_TYPE.OPEN,
  WIFI_SECURITY_TYPE.WPA2_PERSONAL,
  WIFI_SECURITY_TYPE.WPA3_PERSONAL,
  WIFI_SECURITY_TYPE.WPA2_WPA3_PERSONAL,
  WIFI_SECURITY_TYPE.WPA2_ENTERPRISE,
  WIFI_SECURITY_TYPE.WPA3_ENTERPRISE,
];

/** An open network has no password; every other type needs one. */
export function wifiRequiresPassword(securityType: WifiSecurityType): boolean {
  return securityType !== WIFI_SECURITY_TYPE.OPEN;
}

/** 802.11 caps an SSID at 32 bytes. The password is deliberately unrestricted beyond "present". */
export const MAX_WIFI_SSID_LENGTH = 32;
export const MAX_WIFI_INFO_LENGTH = 300;
export const MAX_WIFI_NETWORKS = 20;

/**
 * One network on `hotels/{hotelId}/settings/wifi`.
 *
 * The password is stored and shown in plain text on both sides — that is the
 * feature: a guest reads it off the page and types it into their phone, and
 * the owner needs to see what they typed. It is guest-grade information, not
 * a credential the way an FCM token or the Agora certificate is.
 *
 * `applicableRooms` holds room **ids**, not numbers: a room's number can be
 * edited, its id cannot, and the guest's session carries the id.
 */
export interface WifiNetwork {
  id: string;
  ssid: string;
  /** Empty string for an open network. */
  password: string;
  securityType: WifiSecurityType;
  additionalInfo?: string;
  applicableRooms: string[];
  enabled: boolean;
}

/** `hotels/{hotelId}/settings/wifi` */
export interface WifiSettingsDoc {
  enabled: boolean;
  networks: WifiNetwork[];
  updatedAt?: unknown;
}

export function resolveWifiSettings(
  doc: { enabled?: unknown; networks?: unknown } | null | undefined,
  hotelFlagEnabled: boolean,
): { enabled: boolean; networks: WifiNetwork[] } {
  if (!doc || !Array.isArray(doc.networks)) {
    return { enabled: hotelFlagEnabled, networks: [] };
  }
  return {
    enabled: typeof doc.enabled === 'boolean' ? doc.enabled : hotelFlagEnabled,
    networks: doc.networks as WifiNetwork[],
  };
}

/** What a guest in `roomId` may see: enabled networks whose rooms include theirs. */
export function wifiNetworksForRoom(
  networks: readonly WifiNetwork[],
  roomId: string,
): WifiNetwork[] {
  return networks.filter(
    (network) => network.enabled && network.applicableRooms.includes(roomId),
  );
}

/* -------------------------------------------------------------------------- */
/* Hotel store                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The hotel store sells things — toiletries, souvenirs, the hotel's own
 * products — and is shaped exactly like the restaurant menu: a category list
 * on `settings/store` and one document per product under
 * `settings/store/items`. Same callables (`saveStoreItem` and friends), same
 * guest ordering flow, same request type. What differs is the vocabulary and
 * that a product has no vegetarian mark or preparation time.
 */
export const DEFAULT_STORE_CATEGORIES: readonly string[] = [
  'Toiletries',
  'Souvenirs',
  'Special Products',
];

/** `hotels/{hotelId}/settings/store` */
export interface StoreSettingsDoc {
  enabled: boolean;
  categories: string[];
  updatedAt?: unknown;
}

export function resolveStoreSettings(
  doc: { enabled?: unknown; categories?: unknown } | null | undefined,
  hotelFlagEnabled: boolean,
): { enabled: boolean; categories: string[] } {
  return resolveCategorySettings(doc, hotelFlagEnabled, DEFAULT_STORE_CATEGORIES);
}

/** `hotels/{hotelId}/settings/store/items/{itemId}` */
export interface StoreItemDoc {
  name: string;
  category: string;
  price: number;
  enabled: boolean;
  description?: string;
  /** Product photos, paths under `storeImageStoragePath(hotelId)`; the first is the thumbnail. */
  images?: MenuImageRef[];
  createdAt?: unknown;
  updatedAt?: unknown;
}
